import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";

type TourStep = {
  id: string;
  title: string;
  description: string;
};

const PODCAST_TOUR_STORAGE_KEY = "podcastTourState";

const PODCAST_TOUR_STEPS: TourStep[] = [
  {
    id: "dashboard-series",
    title: "Welcome to Podcast Studio",
    description: "All of your podcast series live here. Use New Series to start a season plan.",
  },
  {
    id: "series-board",
    title: "Plan each season",
    description: "Drag cards between lanes as episodes move from idea to publish-ready.",
  },
  {
    id: "episode-workspace",
    title: "Write scripts & notes",
    description: "Draft scripts, attach context, and keep notes for each episode.",
  },
  {
    id: "review-tab",
    title: "AI Review (Pro)",
    description: "Send episodes through fact-check and critique workflows when you’re on Pro.",
  },
  {
    id: "audio-tab",
    title: "Audio editing",
    description: "Mix narration, music, and SFX before you publish.",
  },
  {
    id: "publish-tab",
    title: "Publish & promo",
    description: "Extract clips, prep metadata, and push episodes live.",
  },
];

type TourState = {
  currentStepId: string | null;
  completed: boolean;
};

const DEFAULT_TOUR_STATE: TourState = {
  currentStepId: PODCAST_TOUR_STEPS[0]?.id ?? null,
  completed: PODCAST_TOUR_STEPS.length === 0,
};

type PodcastTourContextValue = {
  steps: TourStep[];
  state: TourState;
  advance: (stepId: string) => void;
  skipTour: () => void;
};

const PodcastTourContext = createContext<PodcastTourContextValue | null>(null);

export function PodcastTourProvider({ children }: { children: React.ReactNode }) {
  const [state, setState] = useState<TourState>(() => {
    if (typeof window === "undefined") {
      return DEFAULT_TOUR_STATE;
    }
    try {
      const stored = window.localStorage.getItem(PODCAST_TOUR_STORAGE_KEY);
      if (stored) {
        const parsed = JSON.parse(stored) as TourState;
        if (typeof parsed?.completed === "boolean") {
          return {
            currentStepId: parsed.currentStepId ?? DEFAULT_TOUR_STATE.currentStepId,
            completed: parsed.completed,
          };
        }
      }
    } catch {
      /* ignore storage issues */
    }
    return DEFAULT_TOUR_STATE;
  });

  useEffect(() => {
    if (typeof window === "undefined") {
      return;
    }
    try {
      window.localStorage.setItem(PODCAST_TOUR_STORAGE_KEY, JSON.stringify(state));
    } catch {
      /* ignore */
    }
  }, [state]);

  const advance = useCallback((stepId: string) => {
    setState((prev) => {
      if (prev.completed || prev.currentStepId !== stepId) {
        return prev;
      }
      const currentIndex = PODCAST_TOUR_STEPS.findIndex((step) => step.id === stepId);
      if (currentIndex === -1) {
        return prev;
      }
      const nextIndex = currentIndex + 1;
      if (nextIndex >= PODCAST_TOUR_STEPS.length) {
        return { currentStepId: null, completed: true };
      }
      return { currentStepId: PODCAST_TOUR_STEPS[nextIndex].id, completed: false };
    });
  }, []);

  const skipTour = useCallback(() => {
    setState({ currentStepId: null, completed: true });
  }, []);

  const value = useMemo<PodcastTourContextValue>(
    () => ({
      steps: PODCAST_TOUR_STEPS,
      state,
      advance,
      skipTour,
    }),
    [advance, skipTour, state],
  );

  return <PodcastTourContext.Provider value={value}>{children}</PodcastTourContext.Provider>;
}

export type PodcastTourStepHandle = {
  step: TourStep | undefined;
  isActive: boolean;
  stepIndex: number;
  totalSteps: number;
  completeStep: () => void;
  skipTour: () => void;
};

export function usePodcastTourStep(stepId: string): PodcastTourStepHandle {
  const context = useContext(PodcastTourContext);
  if (!context) {
    throw new Error("usePodcastTourStep must be used within PodcastTourProvider");
  }
  const { steps, state, advance, skipTour } = context;
  const stepIndex = steps.findIndex((step) => step.id === stepId);
  const step = stepIndex >= 0 ? steps[stepIndex] : undefined;
  const isActive = !state.completed && state.currentStepId === stepId;
  const completeStep = useCallback(() => advance(stepId), [advance, stepId]);
  return {
    step,
    isActive,
    stepIndex,
    totalSteps: steps.length,
    completeStep,
    skipTour,
  };
}
